import React, { useEffect, useState, useRef, useCallback } from "react";
import { PDFDocument } from "pdf-lib";
import { Loader2, AlertCircle, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Maximize, PanelRightClose, PanelRightOpen, RefreshCw } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

// Configure PDF.js worker - use unpkg CDN which has all npm versions
// This avoids issues with local file serving and .mjs MIME types
if (typeof window !== "undefined" && pdfjsLib.GlobalWorkerOptions) {
  // Use unpkg CDN for the exact version installed via npm
  pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs";
}

/**
 * PDFFormEditor - Interactive PDF form editor
 * Renders PDF with clickable form fields overlaid as HTML inputs
 */
const PDFFormEditor = ({ pdfUrl, onPdfModified }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfLibDoc, setPdfLibDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [formData, setFormData] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const sidebarRef = useRef(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    loadPdf();
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (pdfDoc && currentPage) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale]);

  // Fit to width logic
  const fitToWidth = useCallback(() => {
    if (containerRef.current && pdfDoc) {
      // Get the width of the container, accounting for potential scrollbar and padding
      const containerWidth = containerRef.current.clientWidth - 64; // -64 for generous padding
      if (containerWidth <= 0) return;

      pdfDoc.getPage(currentPage).then(page => {
        const viewport = page.getViewport({ scale: 1.0 });
        const newScale = containerWidth / viewport.width;
        // Clamp to reasonable values
        setScale(Math.min(Math.max(newScale, 0.6), 2.5));
      });
    }
  }, [pdfDoc, currentPage]);

  // Auto-fit on initial load (when loading completes)
  useEffect(() => {
    if (!loading && pdfDoc && isFirstLoad.current) {
      isFirstLoad.current = false;
      // Small delay to ensure container is layout-stable
      setTimeout(fitToWidth, 150);
    }
  }, [loading, pdfDoc, fitToWidth]);

  const loadPdf = async () => {
    try {
      setLoading(true);
      setError(null);
      isFirstLoad.current = true;

      // Fetch PDF
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Failed to fetch PDF");
      const arrayBuffer = await response.arrayBuffer();

      // Create separate copies for each library to avoid ArrayBuffer detachment issues
      const pdfJsBuffer = arrayBuffer.slice(0);
      const pdfLibBuffer = arrayBuffer.slice(0);

      // Load with PDF.js for rendering
      const loadingTask = pdfjsLib.getDocument({ data: pdfJsBuffer });
      const pdfDocument = await loadingTask.promise;
      setPdfDoc(pdfDocument);
      setNumPages(pdfDocument.numPages);

      // Load with pdf-lib for form field extraction and modification
      const pdfLibDocument = await PDFDocument.load(pdfLibBuffer);
      setPdfLibDoc(pdfLibDocument);

      // Extract form fields
      try {
        const form = pdfLibDocument.getForm();
        const fields = form.getFields();

        const fieldInfo = [];
        for (const field of fields) {
          const name = field.getName();
          const type = field.constructor.name;

          let value = "";
          try {
            if (type === "PDFTextField") {
              value = field.getText() || "";
            } else if (type === "PDFCheckBox") {
              value = field.isChecked();
            } else if (type === "PDFRadioGroup") {
              value = field.getSelected() || "";
            } else if (type === "PDFDropdown") {
              value = field.getSelected() || "";
            }
          } catch (e) {
            console.warn(`Could not read value for field ${name}:`, e);
          }

          fieldInfo.push({
            name,
            type,
            value,
            field
          });
        }

        setFormFields(fieldInfo);

        // Initialize form data with existing values
        const initialData = {};
        fieldInfo.forEach(f => {
          initialData[f.name] = f.value;
        });
        setFormData(initialData);

        // If we have fields, show sidebar by default
        setShowSidebar(fieldInfo.length > 0);

      } catch (err) {
      }

      setLoading(false);
    } catch (err) {
      console.error("Error loading PDF:", err);
      setError(err.message || "Failed to load PDF");
      setLoading(false);
    }
  };

  const renderPage = async (pageNumber) => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      // Cancel any pending render task
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }

      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      renderTaskRef.current = page.render(renderContext);
      await renderTaskRef.current.promise;
      renderTaskRef.current = null;

    } catch (err) {
      if (err.name === 'RenderingCancelledException') {
        // Ignore cancellation errors
        return;
      }
      console.error("Error rendering page:", err);
    }
  };

  const handleFieldChange = useCallback(async (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Update the pdf-lib document
    if (!pdfLibDoc) return;

    try {
      const fieldInfo = formFields.find(f => f.name === fieldName);
      if (!fieldInfo) return;

      const { type, field } = fieldInfo;

      if (type === "PDFTextField") {
        field.setText(String(value));
      } else if (type === "PDFCheckBox") {
        if (value) {
          field.check();
        } else {
          field.uncheck();
        }
      } else if (type === "PDFRadioGroup" && value) {
        field.select(value);
      } else if (type === "PDFDropdown" && value) {
        field.select(value);
      }

      // Generate updated PDF blob
      const pdfBytes = await pdfLibDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });

      // Notify parent
      if (onPdfModified && typeof onPdfModified === 'function') {
        onPdfModified(blob);
      }
    } catch (err) {
      console.error("Error updating field:", err);
    }
  }, [pdfLibDoc, formFields, onPdfModified]);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 4));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  const goToPage = (page) => {
    if (page >= 1 && page <= numPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 bg-slate-950 w-full">
        <div className="relative">
          <div className="absolute inset-0 bg-sky-500 blur-xl opacity-20 rounded-full animate-pulse"></div>
          <Loader2 className="w-12 h-12 animate-spin text-sky-400 relative z-10" />
        </div>
        <p className="text-slate-400 mt-6 font-medium tracking-wide">Initializing document viewer...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 bg-slate-950 w-full">
        <div className="p-4 bg-red-500/10 rounded-full mb-6 border border-red-500/20">
          <AlertCircle className="w-10 h-10 text-red-500" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Unable to load document</h3>
        <p className="text-slate-400 text-center max-w-md">{error}</p>
        <button
          onClick={() => loadPdf()}
          className="mt-6 flex items-center gap-2 px-6 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-white font-medium transition-all"
        >
          <RefreshCw className="w-4 h-4" /> Try Again
        </button>
      </div>
    );
  }

  const hasFormFields = formFields.length > 0;

  return (
    <div className="flex flex-col h-full bg-slate-950 w-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 bg-slate-900 border-b border-slate-800 shadow-sm shrink-0 z-10">

        {/* Left: View Controls */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-950 rounded-lg border border-slate-800 p-1">
            <button
              onClick={handleZoomOut}
              className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <div className="h-4 w-px bg-slate-800 mx-1"></div>
            <span className="text-xs font-mono text-slate-300 min-w-[40px] text-center select-none">
              {Math.round(scale * 100)}%
            </span>
            <div className="h-4 w-px bg-slate-800 mx-1"></div>
            <button
              onClick={handleZoomIn}
              className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={fitToWidth}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors border border-transparent hover:border-slate-700"
            title="Fit to Width"
          >
            <Maximize className="w-4 h-4" />
          </button>
        </div>

        {/* Center: Pagination */}
        {numPages > 1 && (
          <div className="flex items-center gap-3 bg-slate-950 rounded-full border border-slate-800 px-4 py-1.5 shadow-inner">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-slate-300 select-none">
              Page <span className="text-white">{currentPage}</span> of {numPages}
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= numPages}
              className="text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Right: Sidebar Toggle */}
        <div className="flex items-center gap-4">
          {hasFormFields ? (
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border transition-all ${showSidebar
                  ? "bg-sky-500/10 border-sky-500/50 text-sky-400"
                  : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"
                }`}
            >
              {showSidebar ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
              {showSidebar ? "Hide Fields" : "Show Fields"}
            </button>
          ) : (
            <div className="text-xs text-slate-500 font-medium px-2">Read-only View</div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden relative">
        <div className="flex h-full">

          {/* PDF Canvas Area */}
          <div
            className="flex-1 overflow-auto bg-slate-950 flex justify-center p-8 custom-scrollbar relative"
            ref={containerRef}
          >
            {/* Canvas Wrapper with Shadow */}
            <div
              className="relative bg-white shadow-2xl shadow-black ring-1 ring-white/10"
              style={{
                // Use a slightly flexible dimension to avoid rigid locking
                minWidth: canvasRef.current ? canvasRef.current.width : 'auto',
                minHeight: canvasRef.current ? canvasRef.current.height : 'auto',
              }}
            >
              <canvas
                ref={canvasRef}
                className="block"
              />
            </div>
          </div>

          {/* Sidebar Panel */}
          {hasFormFields && (
            <div
              ref={sidebarRef}
              className={`
                    border-l border-slate-800 bg-slate-900 transition-all duration-300 ease-in-out flex flex-col z-20
                    ${showSidebar ? "w-[320px] translate-x-0" : "w-0 translate-x-full opacity-0 overflow-hidden border-none"}
                `}
            >
              <div className="p-5 border-b border-slate-800 bg-slate-900 sticky top-0 z-10">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-sky-500 rounded-full"></span>
                  Smart Fill
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Review and edit detected form fields.
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar bg-slate-900/50">
                {formFields.map((field) => {
                  const value = formData[field.name] !== undefined ? formData[field.name] : field.value;

                  // Clean up Detection Name for display
                  let displayName = field.name;
                  try {
                    const parts = displayName.split('.');
                    if (parts.length > 0) displayName = parts[parts.length - 1];
                    displayName = displayName.replace(/\[\d+\]/g, '').replace(/^#/, '').replace(/[_\.]/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                    if (displayName.length < 2) displayName = field.name;
                  } catch (e) { }

                  return (
                    <div key={field.name} className="group">
                      {field.type === "PDFCheckBox" ? (
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 bg-slate-950/50 hover:border-slate-700 hover:bg-slate-800 transition-colors cursor-pointer">
                          <div className="relative flex items-center">
                            <input
                              type="checkbox"
                              checked={value || false}
                              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                              className="peer sr-only"
                            />
                            <div className="w-5 h-5 rounded border border-slate-600 bg-slate-900 peer-checked:bg-sky-500 peer-checked:border-sky-500 transition-all flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1 pt-0.5">
                            <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors select-none">
                              {displayName}
                            </span>
                          </div>
                        </label>
                      ) : (
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">
                            {displayName}
                          </label>
                          <input
                            type="text"
                            value={value || ""}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                            className="w-full px-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all placeholder-slate-700"
                            placeholder="Empty"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFFormEditor;
